import { createApi } from '@reduxjs/toolkit/query/react';

import { IAppFeature } from '../../types/interfaces/app-feature';
import { IAppVariable } from '../../types/interfaces/app-variable';
import { IExerciseLoadFormulaWithNameKeys } from '../../types/interfaces/exercise-load-formula';
import { IFilePaths } from '../../types/interfaces/file-paths';
import {
  IAppLinkUpdate,
  IVersionInfo,
} from '../../types/interfaces/version-info';
import { APP_VARIABLES, HTTP_METHOD, REDUX_CACHE } from '../constants';
import * as endpoints from '../endpoints';
import {
  TransformedErrorResponseWithNotification,
  getBaseQuery,
  getListProvidesTags,
} from '../generic';

export const appConfigApi = createApi({
  reducerPath: 'appConfigApi',
  tagTypes: [
    APP_VARIABLES.APP_VARIABLES,
    APP_VARIABLES.APP_FEATURES,
    APP_VARIABLES.APP_VARIABLE,
    APP_VARIABLES.APP_FORMULAS,
    REDUX_CACHE.ABORT_INVALIDATION,
    REDUX_CACHE.LIST,
  ],
  baseQuery: getBaseQuery(endpoints.AppConfig.ROOT),
  keepUnusedDataFor: 60,
  endpoints: (builder) => ({
    getAppVariablesApi: builder.query<IAppVariable[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppConfig.VARIABLES,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, APP_VARIABLES.APP_VARIABLES),
      ...TransformedErrorResponseWithNotification(),
    }),
    getAppFeaturesApi: builder.query<IAppFeature[], void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppConfig.FEATURES,
          method: HTTP_METHOD.GET,
        };
      },
    }),
    getFormulasApi: builder.query<IExerciseLoadFormulaWithNameKeys[], void>({
      keepUnusedDataFor: 3600,
      query(params) {
        return {
          url: `${endpoints.AppConfig.FORMULAS}`,
          method: HTTP_METHOD.GET,
        };
      },
      providesTags: (result) =>
        getListProvidesTags(result, APP_VARIABLES.APP_FORMULAS),
      ...TransformedErrorResponseWithNotification(),
    }),
    getVersionApi: builder.query<IVersionInfo, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppConfig.VERSION,
          method: HTTP_METHOD.GET,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getAppLinkUpdateApi: builder.query<IAppLinkUpdate, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppConfig.APP_LINK_UPDATE,
          method: HTTP_METHOD.GET,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
    getFilePathsApi: builder.query<IFilePaths, void>({
      keepUnusedDataFor: 3600,
      query() {
        return {
          url: endpoints.AppConfig.FILE_PATHS,
          method: HTTP_METHOD.GET,
        };
      },
      ...TransformedErrorResponseWithNotification(),
    }),
  }),
});

export const {
  useGetAppVariablesApiQuery: useGetAppVariables,
  useGetAppFeaturesApiQuery: useGetAppFeatures,
  useGetFormulasApiQuery: useGetFormulas,
  useGetVersionApiQuery: useGetVersion,
  useGetAppLinkUpdateApiQuery: useGetAppLinkUpdate,
  useGetFilePathsApiQuery: useGetFilePaths,
} = appConfigApi;
